import {mix, Loadable, Eventable} from '../mixin';

const activeLoaders = [];
const {setInterval, clearInterval, Array, Image} = globalThis;

let intervalId = null;
let loading = false;

const update = function()
{
    let i = activeLoaders.length;
    while (i--)
    {
        activeLoaders[i]._updateProgress();
    }
};

const startLoading = function()
{
    if (loading) { return; }
    if (intervalId === null)
    {
        intervalId = setInterval(update, 200);
    }
    loading = true;
};

const stopLoading = function()
{
    if (!loading) { return; }
    if (intervalId !== null)
    {
        clearInterval(intervalId);
        intervalId = null;
    }
    loading = false;
};

const activateLoader = function(loader)
{
    if (activeLoaders.indexOf(loader) === -1)
    {
        activeLoaders.push(loader);
        startLoading();
    }
};

const deactivateLoader = function(loader)
{
    const index = activeLoaders.indexOf(loader);
    if (index !== -1)
    {
        activeLoaders.splice(index, 1);
        if (activeLoaders.length === 0)
        {
            stopLoading();
        }
    }
};

const createImageElement = function(src)
{
    const image = new Image();
    image.src = src;
    image.isSvg = src.match(/svg$/i) !== null;
    return image;
};

/**
 * @typedef {Object} ImageLoader~Entry
 * @property {string} id Id der Bild-Datei,
 * @property {string} src Pfad zur Bild-Datei.
 */
class ImageLoader extends mix().with(Loadable, Eventable)
{
    /**
     * @param {string|string[]|Image[]|ImageLoader~Entry[]} [images] Bilder, die geladen werden sollen. Entweder nur
     * eine Pfadangabe (= id) oder eine Image-Instanz. Oder ein Array von Pfadangaben, Image-Instanzen oder
     * Objekten mit ``id`` und ``src``. Übergibt man keinen Parameter, ist die Loader pausiert und muss manuell
     * gestartet werden.
     * @mixes Loadable
     * @mixes Eventable
     * @see ImageLoader~Entry
     */
    constructor(images)
    {
        super();

        this._paused = true;
        this._images = {};
        this._imagesToLoad = [];

        if (arguments.length === 0) { return; }

        if (!Array.isArray(images))
        {
            this.add(images);
        }
        else
        {
            if (images.length === 0)
            {
                this.addLoad().completeLoad();
                return;
            }
            images.forEach(image => this.add(image));
        }

        this.start();
    }

    /** Startet das Loading, falls Loader pausiert ist.
     * @return {ImageLoader}
     */
    start()
    {
        if (this._paused)
        {
            activateLoader(this);
            this._paused = false;
        }
        return this;
    }

    /** Pausiert das Loading, falls Loader gestartet ist.
     * @return {ImageLoader}
     */
    pause()
    {
        if (!this._paused)
        {
            deactivateLoader(this);
            this._paused = true;
        }
        return this;
    }

    /** Füge ein zu ladendes Bild hinzu.
     * @param {string|Image|ImageLoader~Entry} image Eine Pfadangabe, eine Image-Instanz oder ein Objekt mit ``id`` und ``src``.
     * @return {ImageLoader}
     */
    add(image)
    {
        const type = typeof image;
        let element;
        if (image instanceof Image)
        {
            element = image;
            this._images[image.src] = element;
        }
        else if (type === 'string')
        {
            element = createImageElement(image);
            this._images[image] = element;
        }
        else if (type === 'object')
        {
            element = createImageElement(image.src);
            this._images[image.id] = element;
        }
        this._imagesToLoad.push(element);
        return this.addLoad();
    }

    _updateProgress()
    {
        const imagesToLoad = this._imagesToLoad;
        let i = imagesToLoad.length;
        while (i--)
        {
            const image = imagesToLoad[i];
            if (image.complete && (image.isSvg || image.naturalWidth > 0))
            {
                imagesToLoad.splice(i, 1);
                this.completeLoad();
            }
        }

        if (this.isLoaded())
        {
            this.pause();
        }
    }

    /** Liefert ein geladenes Bild anhand der Id zurück.
     * @param {string} id
     */
    get(id)
    {
        return this._images[id] || null;
    }

    /** Liefert alle geladenen Bilder zurück.
     */
    getAll()
    {
        return this._images;
    }
}

export default ImageLoader;
